/**
 * User Service
 * Handles all user-related API calls
 */

const API_URL = import.meta.env.VITE_API_URL;

export interface User {
  userId: string;
  email: string;
  displayName: string;
  avatarUrl?: string;
  bio?: string;
  isAdmin?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateProfileRequest {
  displayName?: string;
  bio?: string;
}

export interface AvatarUploadUrlRequest {
  contentType: string;
  fileSize: number;
}

export interface AvatarUploadUrlResponse {
  uploadUrl: string;
  avatarKey: string;
  expiresIn: number;
}

export interface ConfirmAvatarUploadRequest {
  avatarKey: string;
}

export interface ConfirmAvatarUploadResponse {
  avatarUrl: string;
}

class UserService {
  /**
   * Get current user profile
   */
  async getMe(token: string): Promise<User> {
    const response = await fetch(`${API_URL}/users/me`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to fetch user profile');
    }

    return response.json();
  }

  /**
   * Update current user profile
   */
  async updateMe(token: string, data: UpdateProfileRequest): Promise<User> {
    const response = await fetch(`${API_URL}/users/me`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to update profile');
    }

    return response.json();
  }

  /**
   * Get presigned URL for avatar upload
   */
  async getAvatarUploadUrl(
    token: string,
    data: AvatarUploadUrlRequest
  ): Promise<AvatarUploadUrlResponse> {
    const response = await fetch(`${API_URL}/users/me/avatar/upload-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to get upload URL');
    }

    return response.json();
  }

  /**
   * Upload avatar to S3
   */
  async uploadAvatar(uploadUrl: string, file: File): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
        'Content-Length': file.size.toString(),
      },
      body: file,
    });

    if (!response.ok) {
      throw new Error('Failed to upload avatar to S3');
    }
  }

  /**
   * Confirm avatar upload
   */
  async confirmAvatarUpload(
    token: string,
    data: ConfirmAvatarUploadRequest
  ): Promise<ConfirmAvatarUploadResponse> {
    const response = await fetch(`${API_URL}/users/me/avatar/confirm`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to confirm avatar upload');
    }

    return response.json();
  }

  /**
   * Get all users (family members list)
   */
  async getAllUsers(token: string): Promise<{ items: User[]; total: number }> {
    const response = await fetch(`${API_URL}/users`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to fetch users');
    }

    return response.json();
  }

  /**
   * Get user profile by userId
   */
  async getUserById(token: string, userId: string): Promise<User> {
    const response = await fetch(`${API_URL}/users/${userId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to fetch user');
    }

    return response.json();
  }
}

export const userService = new UserService();
